import {
  CancellationDueDeath,
  ChangeControlDataForm,
  DeathDataForm,
  FamilySubject,
  JudgmentDataForm,
} from 'cancellations/view-models/cancellation-due-death';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { OkResponse, SubjectData } from 'shell/models/person';
import { transformSubjectAttributes } from 'shell/utils/consult-person/response-transformer/registry-office/subject-attributes';
import { transformBirthdayDocument } from 'shell/utils/consult-person/response-transformer/registry-office/birthday-document';
import { transformCitizenship } from 'shell/utils/consult-person/response-transformer/registry-office/citizenship';
import { transformOtherCitizenship } from 'shell/utils/consult-person/response-transformer/registry-office/other-citizenship';
import { transformParenthood } from 'shell/utils/consult-person/response-transformer/registry-office/parenthood';
import { AccordionProfessionTitle } from 'shell/view-models/accordion-profession-title';
import { DateTime } from 'luxon';
import { transformPerson } from 'shell/utils/consult-person/response-transformer/person/person';
import { transformFamilyCoexistence } from 'shell/utils/consult-person/response-transformer/family/family-coexistence';
import { ActType } from 'shell/constants/act-type';
import { emptyPaper } from 'shell/components/shared/act/constants';
import { CivilStatusID } from 'shell/constants/civil-status';
import { transformDocumentDigital } from 'shell/utils/consult-person/request-transformer/shared/transform-document-digital';
import { Generality } from 'shell/models/person/generality';
import { CodeStepButtonsCancellation } from 'cancellations/constants/code-step-buttons-cancellation';
import { ISTAT_MILANO } from 'shell/constants/istat-milano';

const emptyChangeControlDataForm: ChangeControlDataForm = {
  cancellationReason: '',
  effectiveDate: DateTime.now(),
};

const emptyDeathDataForm: DeathDataForm = {
  actType: ActType.AttoCartaceo,
  paper: emptyPaper,
  eventDate: {
    type: ['day', 'month', 'year'],
    date: null,
  },
  eventForeign: null,
  eventItaly: null,
  exceptionalPlace: '',
  happenInItaly: false,
  office: '',
};

export const emptyItalianAuthority = {
  municipalityId: '',
  municipalityDescription: '',
  province: '',
  provinceCode: '',
  location: '',
};

export const emptyForeignAuthority = {
  stateId: '',
  stateDescription: '',
  municipalityId: '',
  municipalityDescription: '',
  province: '',
  istatCode: '',
};

const emptyJudgmentDataForm: JudgmentDataForm = {
  authority: '',
  courtType: '',
  isItalianAuthority: true,
  judgmentDate: null,
  judgmentNumber: '',
  validityDate: null,
  foreignAuthority: emptyForeignAuthority,
  italianAuthority: emptyItalianAuthority,
};

const transformFamilyData = (family: Array<SubjectData> = [], fiscalCode: string) => {
  return {
    fiscalCodeHouseholder: '',
    members: family,
    family: family
      .filter((subject) => subject.generalita?.codiceFiscale?.codFiscale !== fiscalCode)
      .reduce((acc, subject) => {
        const [residence] = subject.residenza || [];
        return {
          ...acc,
          [subject.generalita?.codiceFiscale?.codFiscale || '']: {
            fiscalCode: subject.generalita?.codiceFiscale?.codFiscale || '',
            surname: subject.generalita?.cognome,
            name: subject.generalita?.nome,
            sex: subject.generalita?.sesso,
            birthday: subject.generalita?.dataNascita
              ? DateTime.fromISO(subject.generalita?.dataNascita)
              : undefined,
            idSchedaSoggettoANPR: subject.generalita?.idSchedaSoggettoANPR || '',
            idIntestatario: subject.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
            birthPlace: subject.generalita?.luogoNascita?.comune?.nomeComune,
            residence: residence?.indirizzo?.comune?.nomeComune || '',
            relation: subject.legameSoggetto?.codiceLegame,
            address: `${residence?.indirizzo?.toponimo?.specie || ''} ${
              residence?.indirizzo?.toponimo?.denominazioneToponimo || ''
            } ${residence?.indirizzo?.numeroCivico?.numero || ''}`,
          },
        };
      }, {}) as { [key: string]: FamilySubject },
  };
};

export const transformAnprResponse = (
  response: OkResponse,
  family: SubjectData[]
): CancellationDueDeath => {
  const responseToTransform = response || {};
  const subjectData = responseToTransform.listaSoggetti?.datiSoggetto[0] || {};
  //const headingResponse = responseToTransform.testataRisposta;
  const personForm = transformPerson(subjectData);
  const familyCoexistence = transformFamilyCoexistence(subjectData);
  const changeControlDataForm = emptyChangeControlDataForm;
  const deathDataForm = emptyDeathDataForm;
  const generalityForm = transformGenerality(subjectData);
  const subjectAttributesForm = transformSubjectAttributes(subjectData);
  const birthdayDocumentForm = transformBirthdayDocument(subjectData);
  const citizenshipForm = transformCitizenship(subjectData);
  const otherCitizenship = transformOtherCitizenship(subjectData);
  const parenthoodForm = transformParenthood(subjectData);
  const professionForm = transformProfessionForm(subjectData);
  const judgmentDataForm = emptyJudgmentDataForm;
  const showSubjectListSection =
    subjectData.legameSoggetto?.codiceLegame === '1' &&
    subjectData.legameSoggetto?.tipoLegame === '3' &&
    family?.length > 1;

  const showCheckboxDeathResident =
    subjectData.generalita?.idSchedaSoggettoComune?.idSchedaSoggettoComuneIstat === ISTAT_MILANO &&
    (subjectData.statoCivile?.statoCivile === CivilStatusID.coniugato ||
      subjectData.statoCivile?.statoCivile === CivilStatusID.unitoCivilmente);

  return {
    activeStep: CodeStepButtonsCancellation.datiCancellazione,
    subjectData: {
      ...subjectData,
      matrimonio: {
        ...subjectData.matrimonio,
        coniuge: {
          ...subjectData.matrimonio?.coniuge,
        },
      },
    },
    person: personForm,
    changeControlDataForm,
    deathDataForm,
    generalityForm,
    subjectAttributesForm,
    birthdayDocumentForm,
    citizenship: citizenshipForm,
    otherCitizenship,
    parenthoodForm,
    judgmentDataForm,
    showSubjectListSection,
    familyCoexistence,
    professionForm,
    showCheckboxDeathResident,
    defaultSubjectListForm: transformFamilyData(family, personForm?.fiscalCode || ''),
    subjectListForm: transformFamilyData(family, personForm?.fiscalCode || ''),
  };
};

const transformProfessionForm = (subjectData: SubjectData): AccordionProfessionTitle => ({
  educationalQualification: subjectData?.altriDati?.titoloStudio,
  professionalPosition: subjectData?.altriDati?.posizioneProfessionale,
  unprofessionalCondition: subjectData?.altriDati?.condizioneNonProfessionale,
});

export const transformAnprRequestDeath = (
  form: CancellationDueDeath,
  anprResponse: OkResponse,
  family: SubjectData[],
  fiscalCode: string,
  coniuge?: boolean
): unknown => {
  const withActPaper =
    form.deathDataForm?.office ||
    form.deathDataForm?.paper?.actYear ||
    form.deathDataForm?.paper?.actPart ||
    form.deathDataForm?.paper?.actSeries ||
    form.deathDataForm?.paper?.actNumber ||
    form.deathDataForm?.paper?.actVolume ||
    form.deathDataForm?.paper?.actDate ||
    form.deathDataForm?.paper?.actMunicipality?.description ||
    form.deathDataForm?.paper?.actMunicipality?.id ||
    form.deathDataForm?.paper?.actMunicipality?.province;

  const withActDigital = Boolean(form.deathDataForm?.digital);

  return {
    morteConiugiResidenti: form.deathDataForm?.deathPartnerResident || coniuge,
    risposta3002: anprResponse.listaSoggetti.datiSoggetto[0],
    richiesta2001: {
      altriDati: {
        condizioneNonProfessionale: form.professionForm?.unprofessionalCondition || undefined,
        posizioneProfessionale: form.professionForm?.professionalPosition || undefined,
        titoloStudio: form.professionForm?.educationalQualification || undefined,
      },
      datiControllo: {
        ruoloSoggetto:
          anprResponse.listaSoggetti.datiSoggetto[0].legameSoggetto?.codiceLegame === '1'
            ? '1'
            : '2',
      },
      datiDecesso: {
        attoEventoANPR:
          withActPaper || withActDigital
            ? {
                atto: withActPaper
                  ? {
                      ufficioMunicipio: form.deathDataForm?.office || undefined,
                      anno: form.deathDataForm?.paper?.actYear || undefined,
                      parte: form.deathDataForm?.paper?.actPart || undefined,
                      serie: form.deathDataForm?.paper?.actSeries || undefined,
                      numeroAtto: form.deathDataForm?.paper?.actNumber || undefined,
                      volume: form.deathDataForm?.paper?.actVolume || undefined,
                      dataFormazioneAtto:
                        form.deathDataForm?.paper?.actDate?.toFormat('yyyy-MM-dd') || undefined,
                      trascritto: form.deathDataForm?.paper?.transcribed ? '1' : undefined,
                      comuneRegistrazione: form.deathDataForm?.paper?.actMunicipality
                        ? {
                            nomeComune:
                              form.deathDataForm?.paper?.actMunicipality?.description || undefined,
                            codiceIstat:
                              form.deathDataForm?.paper?.actMunicipality?.id || undefined,
                            siglaProvinciaIstat:
                              form.deathDataForm?.paper?.actMunicipality?.province || undefined,
                          }
                        : undefined,
                    }
                  : undefined,
                attoANSC: withActDigital
                  ? transformDocumentDigital(form.deathDataForm?.digital || undefined)
                  : undefined,
              }
            : undefined,
        dataEvento: form.deathDataForm?.eventDate?.date?.toFormat('yyyy-MM-dd') || null,
        luogoEvento: {
          luogoEccezionale: form.deathDataForm?.exceptionalPlace || null,
          comune:
            !form.deathDataForm?.happenInItaly && form.deathDataForm?.eventItaly
              ? {
                  codiceIstat: form.deathDataForm?.eventItaly?.municipalityId || undefined,
                  descrizioneLocalita: form.deathDataForm?.eventItaly?.location || undefined,
                  nomeComune: form.deathDataForm?.eventItaly?.municipalityDescription || undefined,
                  siglaProvinciaIstat: form.deathDataForm?.eventItaly?.province || undefined,
                }
              : null,
          localita:
            form.deathDataForm?.happenInItaly && form.deathDataForm?.eventForeign
              ? {
                  codiceStato: form.deathDataForm?.eventForeign?.stateId || undefined,
                  descrizioneLocalita:
                    form.deathDataForm?.eventForeign?.municipalityDescription || undefined,
                  descrizioneStato: form.deathDataForm?.eventForeign?.stateDescription || undefined,
                  provinciaContea: form.deathDataForm?.eventForeign?.province || undefined,
                }
              : null,
        },
      },
      datiSentenza:
        form.judgmentDataForm?.authority ||
        form.judgmentDataForm?.judgmentDate ||
        form.judgmentDataForm?.validityDate ||
        form.judgmentDataForm?.courtType ||
        form.judgmentDataForm?.judgmentNumber
          ? {
              autorita: form.judgmentDataForm?.authority || undefined,
              data: form.judgmentDataForm?.judgmentDate?.toFormat('yyyy-MM-dd') || undefined,
              dataValidita:
                form.judgmentDataForm?.validityDate?.toFormat('yyyy-MM-dd') || undefined,
              tipoTribunale: form.judgmentDataForm?.courtType || undefined,
              numero: form.judgmentDataForm?.judgmentNumber || undefined,
            }
          : undefined,
      elencoSoggettiFamiglia: form.showSubjectListSection
        ? family
            .filter((subject) => subject.generalita?.codiceFiscale?.codFiscale !== fiscalCode)
            .map((subject) => {
              let newRelation = form.subjectListForm?.family
                ? form.subjectListForm?.family[subject.generalita?.codiceFiscale?.codFiscale || '']
                    .relation
                : null;

              // Se sposato o convivente nuova relazione di intestatario al coniuge se sono solo loro due
              let marriedSubject: Generality | null | undefined;

              if (form.subjectData?.matrimonio) {
                marriedSubject = form.subjectData?.matrimonio?.coniuge;
              }

              if (form.subjectData?.unioneCivile) {
                marriedSubject = form.subjectData?.unioneCivile?.unitoCivilmente;
              }

              if (
                family.filter((el) => el.generalita?.codiceFiscale?.codFiscale !== fiscalCode)
                  .length === 1 &&
                family.find(
                  (el) =>
                    el.generalita?.codiceFiscale?.codFiscale ===
                    marriedSubject?.codiceFiscale?.codFiscale
                )
              ) {
                newRelation = '1';
              }

              return {
                generalita: subject.generalita,
                autoveicoli: subject.autoveicoli,
                legameSoggetto: {
                  codiceLegame: String(newRelation),
                  dataDecorrenza: subject.legameSoggetto?.dataDecorrenza,
                  progressivoComponente: subject.legameSoggetto?.progressivoComponente,
                  tipoLegame: subject.legameSoggetto?.tipoLegame,
                },
              };
            })
        : null,
      generalitaPerRicerca: {
        idSchedaSoggettoANPR:
          (anprResponse.listaSoggetti.datiSoggetto[0] || {}).generalita?.idSchedaSoggettoANPR ||
          undefined,
        nome: form.person?.name || undefined,
        cognome: form.person?.surname || undefined,
        codiceFiscale: {
          codFiscale: fiscalCode || undefined,
        },
        idSchedaSoggettoComune: {
          idSchedaSoggetto: form.generalityForm?.municipalityIdentifier || undefined,
          idSchedaSoggettoComuneIstat: form.generalityForm?.istatCode || undefined,
        },
      },
      motivoCancellazione: {
        tipoMotivoCancellazione:
          (anprResponse.listaSoggetti.datiSoggetto[0] || {}).generalita?.soggettoAIRE !== 'S'
            ? form.changeControlDataForm?.cancellationReason
            : undefined,
        tipoMotivoCancellazioneAIRE:
          (anprResponse.listaSoggetti.datiSoggetto[0] || {}).generalita?.soggettoAIRE === 'S'
            ? form.changeControlDataForm?.cancellationReason
            : undefined,
      },
      testataRichiesta: {
        dataDecorrenza:
          form.changeControlDataForm?.effectiveDate?.toFormat('yyyy-MM-dd') || undefined,
      },
    },
  };
};
